import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Bookmark,
  Building2,
  Clock3,
  Flag,
  FormInput,
  Globe2,
  ListTree,
} from 'lucide-react';
import { Card, TextInput } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type { HubModuleDefinition } from './hubConfig';
import { PARAMETER_MODULES } from './parameterModules';

export const parametersModuleConfig = {
  module: {
    title: 'Parameters',
    description: 'Lookup values and reference data used across the application.',
  },
};

type ParameterGroupId = 'appointments' | 'reference' | 'forms';

type ParameterIcon = React.ComponentType<{ className?: string }>;

interface ParameterModuleDefinition extends HubModuleDefinition {
  shortDescription: string;
  icon: ParameterIcon;
}

interface ParameterGroupDefinition {
  id: ParameterGroupId;
  title: string;
  moduleIds: string[];
}

const MODULE_UI: Record<
  string,
  { shortDescription: string; icon: ParameterIcon; group: ParameterGroupId }
> = {
  app_status: {
    shortDescription: 'Status labels & colors',
    icon: Flag,
    group: 'appointments',
  },
  app_duration: {
    shortDescription: 'Duration presets',
    icon: Clock3,
    group: 'appointments',
  },
  p_country: {
    shortDescription: 'Country list',
    icon: Globe2,
    group: 'reference',
  },
  tax_authority: {
    shortDescription: 'Tax authority values',
    icon: Building2,
    group: 'reference',
  },
  tags: {
    shortDescription: 'Shared tags',
    icon: Bookmark,
    group: 'reference',
  },
  custom_fields: {
    shortDescription: 'Extra form fields',
    icon: FormInput,
    group: 'forms',
  },
};

const PARAMETER_GROUPS: ParameterGroupDefinition[] = [
  {
    id: 'appointments',
    title: 'Appointments',
    moduleIds: ['app_status', 'app_duration'],
  },
  {
    id: 'reference',
    title: 'Reference data',
    moduleIds: ['p_country', 'tax_authority', 'tags'],
  },
  {
    id: 'forms',
    title: 'Forms',
    moduleIds: ['custom_fields'],
  },
];

function enrichModule(module: HubModuleDefinition): ParameterModuleDefinition {
  const ui = MODULE_UI[module.id];
  return {
    ...module,
    shortDescription: ui?.shortDescription ?? module.description,
    icon: ui?.icon ?? ListTree,
  };
}

interface ParametersNavProps {
  groups: Array<{ id: ParameterGroupId; title: string; modules: ParameterModuleDefinition[] }>;
  activeModuleId: string | undefined;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSelect: (moduleId: string) => void;
}

const ParametersNav: React.FC<ParametersNavProps> = ({
  groups,
  activeModuleId,
  searchTerm,
  onSearchChange,
  onSelect,
}) => {
  const { colors } = useTheme();
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredGroups = React.useMemo(() => {
    if (!normalizedSearch) return groups;
    return groups
      .map((group) => ({
        ...group,
        modules: group.modules.filter((module) => {
          const haystack = `${module.title} ${module.shortDescription} ${module.description}`.toLowerCase();
          return haystack.includes(normalizedSearch);
        }),
      }))
      .filter((group) => group.modules.length > 0);
  }, [groups, normalizedSearch]);

  const allModules = React.useMemo(
    () => filteredGroups.flatMap((group) => group.modules),
    [filteredGroups],
  );

  const renderButton = (item: ParameterModuleDefinition, compact: boolean) => {
    const Icon = item.icon;
    const active = activeModuleId === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => onSelect(item.id)}
        className={cn(
          'text-left transition-all duration-200',
          compact
            ? cn(
                'inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium',
                active
                  ? cn(colors.primary, colors.primaryText, 'border-transparent shadow-sm')
                  : cn(colors.border, colors.textSecondary, colors.surface, colors.interactiveHover),
              )
            : cn(
                'flex w-full items-center gap-3 rounded-xl px-3 py-3',
                active
                  ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                  : cn(colors.textSecondary, colors.interactiveHover),
              ),
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-lg',
            compact ? 'h-4 w-4' : 'h-9 w-9',
            !compact && (active ? 'bg-white/15' : cn(colors.surface, 'border', colors.border)),
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        {compact ? (
          <span>{item.title}</span>
        ) : (
          <span className="min-w-0 flex-1">
            <span className={cn('block text-sm font-medium', active ? colors.primaryText : colors.text)}>
              {item.title}
            </span>
            <span className={cn('mt-0.5 block truncate text-xs', active ? 'opacity-90' : colors.textMuted)}>
              {item.shortDescription}
            </span>
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <div className={cn('border-b p-3 lg:hidden', colors.border)}>
        <TextInput
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search parameters…"
          className="mb-3"
        />
        <div className="flex gap-2 overflow-x-auto pb-1">
          {allModules.map((module) => renderButton(module, true))}
        </div>
      </div>

      <nav
        className={cn(
          'hidden lg:flex lg:w-64 lg:shrink-0 lg:flex-col lg:gap-1 lg:border-r lg:p-3',
          colors.border,
        )}
      >
        <div className="mb-2 px-1">
          <TextInput
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search parameters…"
          />
        </div>

        {filteredGroups.length === 0 ? (
          <p className={cn('px-3 py-4 text-xs', colors.textMuted)}>No parameters match your search.</p>
        ) : (
          filteredGroups.map((group, index) => (
            <div key={group.id} className="flex flex-col gap-1">
              <p
                className={cn(
                  'px-3 pb-1 text-[10px] font-bold uppercase tracking-wider',
                  index > 0 && 'pt-3',
                  colors.textMuted,
                )}
              >
                {group.title}
              </p>
              {group.modules.map((module) => renderButton(module, false))}
            </div>
          ))
        )}
      </nav>
    </>
  );
};

const ParametersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { colors } = useTheme();
  const { user, can, permissionsLoaded } = useAuth();

  const searchTerm = searchParams.get('search') || '';
  const selectedModuleIdFromUrl = searchParams.get('module');

  const canView = React.useCallback(
    (moduleKey: string) => {
      if (user?.role === 'ADMIN' || !permissionsLoaded) {
        return true;
      }
      return can(moduleKey, 'view');
    },
    [can, permissionsLoaded, user?.role],
  );

  const visibleModules = React.useMemo(
    () =>
      PARAMETER_MODULES.filter((module) => {
        if (module.adminOnly && user?.role !== 'ADMIN') {
          return false;
        }
        return canView(module.permissionModuleKey);
      }).map(enrichModule),
    [canView, user?.role],
  );

  const visibleGroups = React.useMemo(() => {
    return PARAMETER_GROUPS.map((group) => ({
      id: group.id,
      title: group.title,
      modules: group.moduleIds
        .map((id) => visibleModules.find((module) => module.id === id))
        .filter((module): module is ParameterModuleDefinition => Boolean(module)),
    })).filter((group) => group.modules.length > 0);
  }, [visibleModules]);

  const selectedModule = React.useMemo(() => {
    if (selectedModuleIdFromUrl) {
      const fromUrl = visibleModules.find((module) => module.id === selectedModuleIdFromUrl);
      if (fromUrl) return fromUrl;
    }
    return visibleGroups[0]?.modules[0] ?? visibleModules[0];
  }, [selectedModuleIdFromUrl, visibleGroups, visibleModules]);

  const updateParams = React.useCallback(
    (next: { module?: string; search?: string }) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next.module !== undefined) {
            if (next.module) {
              params.set('module', next.module);
            } else {
              params.delete('module');
            }
          }
          if (next.search !== undefined) {
            if (next.search.trim()) {
              params.set('search', next.search);
            } else {
              params.delete('search');
            }
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  React.useEffect(() => {
    if (!selectedModule) {
      return;
    }
    if (selectedModule.id !== selectedModuleIdFromUrl) {
      updateParams({ module: selectedModule.id });
    }
  }, [selectedModule, selectedModuleIdFromUrl, updateParams]);

  if (visibleModules.length === 0) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="w-full space-y-6 p-4 sm:p-6">
          <Card className="p-8 text-center">
            <h1 className={cn('text-2xl font-semibold', colors.text)}>Parameters</h1>
            <p className={cn('mt-3 text-sm', colors.textMuted)}>
              No parameter modules are available for your current permissions.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  const SelectedListComponent = selectedModule?.component;

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full space-y-6 p-4 sm:p-6">
        <div className={cn('rounded-2xl border px-5 py-5 sm:px-6', colors.border, colors.surface)}>
          <div className="flex items-start gap-4">
            <span className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', colors.info)}>
              <ListTree className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h1 className={cn('text-lg font-semibold tracking-tight', colors.text)}>Parameters</h1>
              <p className={cn('mt-1 text-sm leading-relaxed', colors.textMuted)}>
                Shared lookup values and form configuration: appointment statuses and durations,
                countries, tax authorities, tags, and custom fields.
              </p>
            </div>
          </div>
        </div>

        <Card className="overflow-hidden">
          <div className="flex min-h-0 flex-col lg:min-h-[36rem] lg:flex-row">
            <ParametersNav
              groups={visibleGroups}
              activeModuleId={selectedModule?.id}
              searchTerm={searchTerm}
              onSearchChange={(value) => updateParams({ search: value })}
              onSelect={(moduleId) => updateParams({ module: moduleId })}
            />

            <div className="min-w-0 flex-1 p-4 sm:p-6">
              {selectedModule && SelectedListComponent ? (
                <div key={selectedModule.id}>
                  {React.createElement(
                    SelectedListComponent as React.ComponentType<{ embedded?: boolean }>,
                    { embedded: true },
                  )}
                </div>
              ) : (
                <p className={cn('text-sm', colors.textMuted)}>
                  Select a parameter list from the navigation.
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ParametersPage;
